import { wrapTool, toolError, toolSuccess } from '../utils/ToolUtils';
import type { AnyToolFunction } from '../types';
import { functions } from '@/services/firebase';
import { httpsCallable } from 'firebase/functions';
import { logger } from '@/utils/logger';
import { getFineTunedModel } from '../fine-tuned-models';
import { importWithRetry } from '@/utils/dynamicImport';

// Module-level cache for ECB exchange rates (updates once daily, cache for 1 hour)
const ECB_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const ECB_STORAGE_KEY = 'ecb_rates_cache';

// Load cached rates from localStorage on startup
function loadEcbCache(): { rates: Record<string, number>; fetchedAt: number } | null {
    try {
        const stored = localStorage.getItem(ECB_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed?.rates && typeof parsed.fetchedAt === 'number') {
                return parsed;
            }
        }
    } catch {
        // localStorage unavailable or corrupted — ignore
    }
    return null;
}

function saveEcbCache(cache: { rates: Record<string, number>; fetchedAt: number }): void {
    try {
        localStorage.setItem(ECB_STORAGE_KEY, JSON.stringify(cache));
    } catch {
        // localStorage full or unavailable — ignore
    }
}

let _ecbCache = loadEcbCache();

export const FinanceTools = {
    analyze_receipt: wrapTool('analyze_receipt', async (args: { image_data: string, mime_type: string }) => {
        const { AutonomousIntelligence, getResponseText } = await importWithRetry(() => import('@/services/intelligence/AutonomousIntelligence'));

        // Construct Multimodal Prompt
        const prompt = `You are an expert accountant. Extract the following data from this receipt image:
        1. Vendor Name
        2. Date
        3. Total Amount
        4. Expense Category (Travel, Equipment, Meals, Marketing, Other)
        5. Brief Description
        
        Output strictly in JSON format: { "vendor": string, "date": string, "amount": number, "category": string, "description": string }`;

        const res = await AutonomousIntelligence.generateContent(
            [
                {
                    role: 'user',
                    parts: [
                        { text: prompt },
                        { inlineData: { mimeType: args.mime_type, data: args.image_data } }
                    ]
                }
            ],
            getFineTunedModel('finance')
        );

        // Access the text from the response object
        const text = getResponseText(res);

        return {
            raw_data: text,
            message: "Receipt analysis completed."
        };
    }),

    audit_distribution: wrapTool('audit_distribution', async (args: { trackTitle: string; distributor: string }) => {
        const { DISTRIBUTORS } = await importWithRetry(() => import('@/core/config/distributors'));

        // 1. Check Distributor Config
        const distConfig = DISTRIBUTORS[args.distributor as keyof typeof DISTRIBUTORS];
        if (!distConfig) {
            return toolError(`Distributor '${args.distributor}' is not in the approved database.`, "UNKNOWN_DISTRIBUTOR");
        }

        return {
            status: "READY_FOR_AUDIT",
            distributor: distConfig.name,
            party_id: distConfig.systemIdentifier,
            message: `Distribution channel '${distConfig.name}' verified. Recipient Party ID: ${distConfig.systemIdentifier}. Ready to generate ERN.`
        };
    }),

    forecast_revenue: wrapTool('forecast_revenue', async (args: { currentStreams: number, platform: string, rightsHolderSplit: number }) => {
        if (!Number.isFinite(args.currentStreams) || args.currentStreams < 0) {
            return toolError('Monthly streams must be a non-negative number.', 'INVALID_STREAM_COUNT');
        }
        if (!Number.isFinite(args.rightsHolderSplit) || args.rightsHolderSplit < 0 || args.rightsHolderSplit > 100) {
            return toolError('Rights-holder split must be between 0 and 100 percent.', 'INVALID_RIGHTS_SPLIT');
        }
        // Industry average payout rates (approximate)
        const PAYOUT_RATES: Record<string, number> = {
            'Spotify': 0.004,
            'Apple Music': 0.008,
            'YouTube Music': 0.002,
            'Amazon Music': 0.004,
            'Tidal': 0.013,
            'Deezer': 0.006,
            'Pandora': 0.001,
            'Other': 0.003
        };

        const rate = PAYOUT_RATES[args.platform] || PAYOUT_RATES['Other']!;
        const grossCents = Math.round(args.currentStreams * rate * 100);

        // Standard management fee is ~20%
        const MANAGER_FEE_PERCENT = 20;

        const projectionsCents = {
            month_1: grossCents,
            month_6: grossCents * 6,
            year_1: grossCents * 12
        };

        const managerFeeSavedCents = {
            month_1: Math.round(projectionsCents.month_1 * (MANAGER_FEE_PERCENT / 100)),
            month_6: Math.round(projectionsCents.month_6 * (MANAGER_FEE_PERCENT / 100)),
            year_1: Math.round(projectionsCents.year_1 * (MANAGER_FEE_PERCENT / 100))
        };

        const netRevenueCents = {
            month_1: Math.round(projectionsCents.month_1 * (args.rightsHolderSplit / 100)),
            month_6: Math.round(projectionsCents.month_6 * (args.rightsHolderSplit / 100)),
            year_1: Math.round(projectionsCents.year_1 * (args.rightsHolderSplit / 100))
        };

        const managerFeeSaved = {
            month_1: managerFeeSavedCents.month_1 / 100,
            month_6: managerFeeSavedCents.month_6 / 100,
            year_1: managerFeeSavedCents.year_1 / 100
        };

        return toolSuccess({
            platform: args.platform,
            rate_per_stream: rate,
            estimateType: 'rough_estimate',
            currency: 'USD',
            confidenceLevel: 'low',
            confidenceSource: 'No distributor statement history, territory mix, subscription mix, fee schedule, or variance data was supplied.',
            assumptions: [
                `${args.currentStreams} streams repeat every month with no growth or decline.`,
                `Illustrative ${args.platform} rate of $${rate.toFixed(4)} per stream remains constant.`,
                `Rights-holder share is ${args.rightsHolderSplit}%.`,
                'The manager-fee comparison is hypothetical at 20%; it is not verified money saved.',
                'Taxes, distributor fees, currency conversion, territory mix, and recoupment are excluded.',
            ],
            projections: {
                gross: { month_1: projectionsCents.month_1 / 100, month_6: projectionsCents.month_6 / 100, year_1: projectionsCents.year_1 / 100 },
                manager_fee_saved: managerFeeSaved,
                net_to_rights_holder: { month_1: netRevenueCents.month_1 / 100, month_6: netRevenueCents.month_6 / 100, year_1: netRevenueCents.year_1 / 100 }
            },
            message: `Rough revenue estimate calculated for ${args.currentStreams} monthly streams on ${args.platform}. Hypothetical annual 20% manager-fee comparison: $${managerFeeSaved.year_1.toFixed(2)}.`
        }, `Rough revenue estimate calculated from static assumptions.`);
    }),

    generate_schedule_c: wrapTool('generate_schedule_c', async (args: { taxYear: number; totalIncome: number; totalExpenses: number; ownerName: string }) => {
        // Simplified Schedule C draft — full IRS-compliant version requires tax API integration
        const incomeCents = Math.round(args.totalIncome * 100);
        const expensesCents = Math.round(args.totalExpenses * 100);
        const netProfitCents = incomeCents - expensesCents;
        const netProfit = netProfitCents / 100;
        const taxPrepMode = "Active";

        return toolSuccess({
            taxYear: args.taxYear,
            ownerName: args.ownerName,
            grossReceipts: args.totalIncome,
            totalExpenses: args.totalExpenses,
            netProfitOrLoss: netProfit,
            formType: 'Schedule C (Form 1040)',
            taxPrepMode
        }, `Schedule C draft generated for ${args.ownerName} (Tax Year ${args.taxYear}). Net Profit: $${netProfit.toFixed(2)}`);
    }),

    calculate_waterfall: wrapTool('calculate_waterfall', async (args: { trackTitle: string; totalRevenue: number; payees: Array<{ name: string; percentage: number; is1099Eligible: boolean }> }) => {
        // Validation
        const totalPercentage = args.payees.reduce((acc, p) => acc + p.percentage, 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
            return toolError("Payee percentages must sum to 100%.", "INVALID_SPLIT");
        }

        const totalRevenueCents = Math.round(args.totalRevenue * 100);

        const waterfall = args.payees.map(p => {
            const payoutCents = Math.round(totalRevenueCents * (p.percentage / 100));
            const payout = payoutCents / 100;
            return {
                name: p.name,
                percentage: p.percentage,
                payout,
                requires1099: p.is1099Eligible && payout >= 600 // Tagging 1099-worthy payouts
            };
        });

        const total1099s = waterfall.filter(w => w.requires1099).length;

        return toolSuccess({
            trackTitle: args.trackTitle,
            totalRevenue: args.totalRevenue,
            waterfall,
            flags: {
                total1099FormsRequired: total1099s
            }
        }, `Waterfall calculated for "${args.trackTitle}". Total Revenue: $${args.totalRevenue}. Flagged ${total1099s} payouts for 1099 processing.`);
    }),

    initiate_split_escrow: wrapTool('initiate_split_escrow', async (args: { trackId: string; holdAmountUsd: number; holdAmount?: number; parties: string[] }) => {
        // Item 135: Initiate split escrow via Stripe Connect Cloud Function.
        // ISSUE-853: amounts are typed — the tool takes USD dollars and converts
        // to integer cents exactly once here; the backend contract is cents.
        try {
            const usd = args.holdAmountUsd ?? args.holdAmount;
            if (typeof usd !== 'number' || !isFinite(usd) || usd <= 0) {
                return toolError('holdAmountUsd must be a positive number of US dollars.', 'INVALID_AMOUNT');
            }
            const amountCents = Math.round(usd * 100);

            const initEscrowFn = httpsCallable<
                { trackId: string; holdAmount: number; parties: string[] },
                { escrowAccount: string; status: string; stripePaymentIntentId?: string; amountCents?: number; amountFormatted?: string; fundsHeld?: boolean }
            >(functions, 'initiateSplitEscrow');

            const result = await initEscrowFn({
                trackId: args.trackId,
                holdAmount: amountCents,
                parties: args.parties
            });

            // Backend fails closed (ISSUE-853): success implies a real PaymentIntent.
            const intentId = result.data.stripePaymentIntentId;
            if (!intentId || result.data.fundsHeld !== true) {
                return toolError(
                    'ESCROW_NOT_FUNDED: Stripe did not confirm a payment intent for this escrow. No funds are held.',
                    'ESCROW_NOT_FUNDED'
                );
            }

            return toolSuccess({
                trackId: args.trackId,
                escrowAccount: result.data.escrowAccount,
                stripePaymentIntentId: intentId,
                amountCents,
                amountFormatted: `$${(amountCents / 100).toFixed(2)}`,
                fundsHeld: true,
                pendingSignaturesFrom: args.parties,
                status: result.data.status
            }, `$${(amountCents / 100).toFixed(2)} held in Stripe escrow (payment intent ${intentId}) until split sign-off is complete from all parties.`);
        } catch (error: unknown) {
            logger.warn('[FinanceTools] Escrow Cloud Function unavailable:', error);
            return toolError(
                'Escrow initiation failed: initiateSplitEscrow is unavailable or misconfigured. No funds were held.',
                'ESCROW_UNAVAILABLE'
            );
        }
    }),

    compare_budget_vs_actuals: wrapTool('compare_budget_vs_actuals', async (args: { projectOrTourName: string; projectedBudget: number; actualExpenses: number; advancesReceived: number }) => {
        // Budget comparison tool (Item 139)
        const projectedBudgetCents = Math.round(args.projectedBudget * 100);
        const actualExpensesCents = Math.round(args.actualExpenses * 100);
        const advancesReceivedCents = Math.round(args.advancesReceived * 100);

        const varianceCents = projectedBudgetCents - actualExpensesCents;
        const netPositionCents = advancesReceivedCents - actualExpensesCents;

        const variance = varianceCents / 100;
        const netPosition = netPositionCents / 100;

        return toolSuccess({
            projectOrTourName: args.projectOrTourName,
            projectedBudget: args.projectedBudget,
            actualExpenses: args.actualExpenses,
            advancesReceived: args.advancesReceived,
            budgetVariance: variance,
            netPosition,
            status: netPosition >= 0 ? 'Surplus' : 'Deficit'
        }, `Budget vs Actuals comparison for ${args.projectOrTourName}: Projected $${args.projectedBudget}, Actual $${args.actualExpenses}. Variance is $${variance > 0 ? '+' : ''}${variance}. Current net position against advances is $${netPosition}.`);
    }),

    predict_daily_royalties: wrapTool('predict_daily_royalties', async (args: { trackId: string; dailyStreams: number; platform: string }) => {
        // Daily royalties prediction using industry average payout rates (Item 152)
        const RATE = args.platform.toLowerCase() === 'spotify' ? 0.0035 : 0.006;
        const predictedMonthlyStreams = args.dailyStreams * 30;
        
        const dailyGrossCents = Math.round(args.dailyStreams * RATE * 100);
        const estimatedMonthlyPayoutCents = dailyGrossCents * 30;
        const estimatedMonthlyPayout = estimatedMonthlyPayoutCents / 100;

        return toolSuccess({
            trackId: args.trackId,
            platform: args.platform,
            currentDailyStreams: args.dailyStreams,
            predictedMonthlyStreams,
            estimatedMonthlyPayout: estimatedMonthlyPayout,
            estimateType: 'rough_estimate',
            currency: 'USD',
            ratePerStream: RATE,
            confidenceLevel: 'low',
            confidenceSource: 'Static illustrative payout rate with no historical distributor statements or platform/territory mix.',
            assumptions: [
                `${args.dailyStreams} streams repeat for 30 consecutive days.`,
                `Illustrative rate of $${RATE.toFixed(4)} per stream remains constant.`,
                'Distributor fees, rights splits, taxes, currency, territory, and subscription mix are excluded.',
            ],
        }, `Rough royalty estimate for ${args.trackId} on ${args.platform}: ${args.dailyStreams} repeated daily streams imply about $${estimatedMonthlyPayout.toFixed(2)} before fees, splits, and taxes.`);
    }),

    convert_multi_currency: wrapTool('convert_multi_currency', async (args: { amount: number; sourceCurrency: string; targetCurrency: string }) => {
        // Multi-Currency Ledger (Item 153)
        // Fetches live exchange rates from the European Central Bank (free, no key required).
        // ECB publishes daily EUR-based rates; we compute cross-rates for any pair.
        let rate: number;
        let source = 'ECB';

        // Validate ISO 4217 currency codes
        const src = args.sourceCurrency.toUpperCase();
        const tgt = args.targetCurrency.toUpperCase();
        if (!/^[A-Z]{3}$/.test(src)) {
            return toolError(`Invalid source currency code: '${args.sourceCurrency}'. Must be 3-letter ISO 4217 (e.g., USD, EUR, GBP).`, 'INVALID_CURRENCY');
        }
        if (!/^[A-Z]{3}$/.test(tgt)) {
            return toolError(`Invalid target currency code: '${args.targetCurrency}'. Must be 3-letter ISO 4217 (e.g., USD, EUR, GBP).`, 'INVALID_CURRENCY');
        }
        if (args.amount <= 0) {
            return toolError(`Amount must be positive. Received: ${args.amount}`, 'INVALID_AMOUNT');
        }

        try {
            // Check cache first
            if (_ecbCache && Date.now() - _ecbCache.fetchedAt < ECB_CACHE_TTL_MS) {
                const rateMap = _ecbCache.rates;
                if (src === tgt) {
                    rate = 1.0;
                } else if (rateMap[src] && rateMap[tgt]) {
                    rate = rateMap[tgt] / rateMap[src];
                } else {
                    throw new Error(`Currency not found in cached ECB data: ${!rateMap[src] ? src : tgt}`);
                }
                source = 'ECB (cached)';
                logger.info(`[FinanceTools] ECB cached rate ${src}→${tgt}: ${rate.toFixed(6)}`);
            } else {
                // Fetch fresh from ECB
                const ecbUrl = 'https://www.ecb.europa.eu/stats/eurofxref/eurofxref-daily.xml';
                const response = await fetch(ecbUrl, { signal: AbortSignal.timeout(5000) });
                if (!response.ok) throw new Error(`ECB API returned ${response.status}`);
                const xml = await response.text();

                // Parse EUR-based rates from XML using regex (lightweight, no XML parser needed)
                const rateMap: Record<string, number> = { EUR: 1.0 };
                const regex = /currency=['"]([A-Z]+)['"]\s+rate=['"]([\d.]+)['"]/g;
                let match: RegExpExecArray | null;
                while ((match = regex.exec(xml)) !== null) {
                    rateMap[match[1]!] = parseFloat(match[2]!);
                }

                // Update in-memory + localStorage cache
                _ecbCache = { rates: rateMap, fetchedAt: Date.now() };
                saveEcbCache(_ecbCache);

                if (src === tgt) {
                    rate = 1.0;
                } else if (rateMap[src] && rateMap[tgt]) {
                    rate = rateMap[tgt] / rateMap[src];
                } else {
                    throw new Error(`Currency not found in ECB data: ${!rateMap[src] ? src : tgt}`);
                }

                source = 'ECB';
                logger.info(`[FinanceTools] ECB live rate ${src}→${tgt}: ${rate.toFixed(6)}`);
            }
        } catch (error: unknown) {
            logger.warn('[FinanceTools] ECB API unavailable:', error);
            return toolError(
                `Currency conversion failed because live ECB rates are unavailable for ${src}->${tgt}.`,
                'EXCHANGE_RATE_UNAVAILABLE'
            );
        }

        const converted = args.amount * rate;

        return toolSuccess({
            sourceAmount: args.amount,
            sourceCurrency: args.sourceCurrency,
            targetCurrency: args.targetCurrency,
            exchangeRate: Number(rate.toFixed(6)),
            convertedAmount: Number(converted.toFixed(2)),
            rateSource: source,
            timestamp: new Date().toISOString()
        }, `Converted ${args.amount} ${args.sourceCurrency} to ${converted.toFixed(2)} ${args.targetCurrency} (Rate: ${rate.toFixed(6)}, Source: ${source}).`);
    }),

    onboard_stripe_connect: wrapTool('onboard_stripe_connect', async (args: { email: string; role: string; splitPercentage: number }) => {
        // Item 154: Onboard collaborator to Stripe Connect via Cloud Function
        try {
            const onboardFn = httpsCallable<
                { email: string; role: string; splitPercentage: number },
                { accountId: string; onboardingUrl: string; status: string }
            >(functions, 'createStripeConnectAccount');

            const result = await onboardFn({
                email: args.email,
                role: args.role,
                splitPercentage: args.splitPercentage
            });

            return toolSuccess({
                email: args.email,
                role: args.role,
                assignedSplit: args.splitPercentage,
                stripeConnectAccountId: result.data.accountId,
                onboardingUrl: result.data.onboardingUrl,
                status: result.data.status
            }, `Stripe Connect custom account onboarding initiated for ${args.email}. Account ID: ${result.data.accountId}.`);
        } catch (error: unknown) {
            logger.warn('[FinanceTools] Stripe Connect Cloud Function unavailable:', error);
            return toolError(
                'Stripe Connect onboarding failed: createStripeConnectAccount is unavailable or misconfigured. No account was created.',
                'STRIPE_CONNECT_UNAVAILABLE'
            );
        }
    }),

    request_tax_forms: wrapTool('request_tax_forms', async (args: { payees: Array<{ name: string; email: string; isUsPerson: boolean }> }) => {
        // Item 155: Request tax forms via Cloud Function
        try {
            const requestFormsFn = httpsCallable<
                { payees: Array<{ name: string; email: string; isUsPerson: boolean }> },
                { requests: Array<{ name: string; email: string; formTypeRequested: string; status: string }> }
            >(functions, 'requestTaxForms');

            const result = await requestFormsFn({ payees: args.payees });

            return toolSuccess({
                payeesProcessed: args.payees.length,
                requests: result.data.requests
            }, `Automated tax form collection initiated for ${args.payees.length} payees. Payouts locked until validated.`);
        } catch (error: unknown) {
            logger.warn('[FinanceTools] Tax forms Cloud Function unavailable:', error);
            return toolError(
                'Tax form collection failed: requestTaxForms is unavailable or provider configuration is missing. No tax form requests were sent.',
                'TAX_FORMS_UNAVAILABLE'
            );
        }
    }),

    // ISSUE-856: this tool never reads CSV file content, parses rows, validates
    // columns, or reconciles totals — args.csvFiles are treated as bare
    // filenames dropped into a prompt asking Gemini to DESCRIBE a mapping
    // conceptually. It used to return status: 'Normalized into standard indii
    // ledger format', letting an AI narrative pass for a deterministic
    // imported ledger. A real CSV ingestion/reconciliation pipeline is a
    // separate, larger build (parser, per-distributor schema mapping,
    // row-level validation, totals reconciliation) — this fix only stops the
    // false completion claim, labeling the output what it actually is: a
    // draft mapping suggestion, not normalized ledger data.
    normalize_distributor_statements: wrapTool('normalize_distributor_statements', async (args: { csvFiles: string[] }) => {
        const { AutonomousIntelligence, getResponseText } = await importWithRetry(() => import('@/services/intelligence/AutonomousIntelligence'));

        const prompt = `
        You are a music industry financial analyst. The following CSV files have been uploaded
        from ${args.csvFiles.length} different music distributors:
        ${args.csvFiles.map((f, i) => `${i + 1}. ${f}`).join('\n')}

        Each distributor uses a different CSV format for royalty statements.
        Describe the canonical normalization mapping that would unify these into a standard format with columns:
        - track_title, artist, isrc, period, streams, revenue, territory, distributor

        Output a JSON summary of the normalization result.
        `;

        try {
            const response = await AutonomousIntelligence.generateContent(prompt, getFineTunedModel('finance'));
            const analysisText = getResponseText(response);

            return toolSuccess({
                filesProcessed: args.csvFiles.length,
                normalizationAnalysis: analysisText,
                status: 'mapping_draft'
            }, `Generated a draft normalization mapping suggestion for ${args.csvFiles.length} distributor statement(s). No files were actually parsed and no ledger rows were created — this is an AI-suggested column mapping only. Real CSV import, row validation, and totals reconciliation still need to happen before this can be trusted as ledger data.`);
        } catch (error: unknown) {
            logger.warn('[FinanceTools] Gemini normalization failed:', error);
            return toolError(
                'Distributor statement normalization failed; no basic-mode ingest was performed.',
                'STATEMENT_NORMALIZATION_FAILED'
            );
        }
    }),

    royalty_distribution_calculator: wrapTool('royalty_distribution_calculator', async (args: {
        trackTitle: string;
        grossRevenue: number;
        currency?: string;
        distributionFeePercent?: number;
        recoupableExpenses?: Array<{ category: string; description?: string; amount: number }>;
        parties: Array<{
            name: string;
            role: string;
            percentage: number;
            shareType?: 'master' | 'publishing' | 'net_profit';
            advancePaid?: number;
            taxWithholdingPercent?: number;
            payoutMethod?: string;
        }>;
    }) => {
        // Validation
        if (!args.trackTitle || typeof args.trackTitle !== 'string') {
            return toolError("Track or release title is required.", "INVALID_TRACK_TITLE");
        }
        if (typeof args.grossRevenue !== 'number' || !Number.isFinite(args.grossRevenue) || args.grossRevenue < 0) {
            return toolError("Gross revenue must be a non-negative number.", "INVALID_GROSS_REVENUE");
        }
        if (!Array.isArray(args.parties) || args.parties.length === 0) {
            return toolError("At least one recipient party must be provided.", "NO_PARTIES_PROVIDED");
        }

        const distFeePercent = args.distributionFeePercent ?? 0;
        if (typeof distFeePercent !== 'number' || !Number.isFinite(distFeePercent) || distFeePercent < 0 || distFeePercent >= 100) {
            return toolError("Distribution fee percentage must be between 0 and 99.99%.", "INVALID_DISTRIBUTION_FEE");
        }

        // Percentage sum validation
        const totalPercentage = args.parties.reduce((acc, p) => acc + (typeof p.percentage === 'number' ? p.percentage : 0), 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
            return toolError(
                `Party split percentages must sum to 100%. Current sum: ${totalPercentage.toFixed(2)}%.`,
                "INVALID_SPLIT_TOTAL",
                { totalPercentage, discrepancy: Number((100 - totalPercentage).toFixed(2)) }
            );
        }

        for (const party of args.parties) {
            if (!party.name || typeof party.name !== 'string') {
                return toolError("All parties must specify a valid name.", "INVALID_PARTY_NAME");
            }
            if (typeof party.percentage !== 'number' || party.percentage < 0) {
                return toolError(`Invalid split percentage for party '${party.name}'. Must be non-negative.`, "INVALID_PARTY_PERCENTAGE");
            }
            if (party.advancePaid !== undefined && (typeof party.advancePaid !== 'number' || party.advancePaid < 0)) {
                return toolError(`Advance paid for '${party.name}' must be a non-negative number.`, "INVALID_ADVANCE_AMOUNT");
            }
            if (party.taxWithholdingPercent !== undefined && (typeof party.taxWithholdingPercent !== 'number' || party.taxWithholdingPercent < 0 || party.taxWithholdingPercent > 100)) {
                return toolError(`Tax withholding percentage for '${party.name}' must be between 0% and 100%.`, "INVALID_WITHHOLDING_PERCENT");
            }
        }

        const currency = (args.currency || 'USD').toUpperCase();

        // High precision math using integer cents
        const grossCents = Math.round(args.grossRevenue * 100);
        const distributionFeeCents = Math.round(grossCents * (distFeePercent / 100));
        const postDistributorCents = grossCents - distributionFeeCents;

        // Recoupable expenses
        const rawExpenses = args.recoupableExpenses ?? [];
        let totalExpensesCents = 0;
        const itemizedExpenses = rawExpenses.map(exp => {
            const amountCents = Math.max(0, Math.round((exp.amount || 0) * 100));
            totalExpensesCents += amountCents;
            return {
                category: exp.category || 'General Expense',
                description: exp.description || '',
                amount: amountCents / 100
            };
        });

        const expensesRecoupedCents = Math.min(postDistributorCents, totalExpensesCents);
        const unrecoupedExpensesCents = totalExpensesCents - expensesRecoupedCents;
        const distributablePoolCents = Math.max(0, postDistributorCents - expensesRecoupedCents);

        let totalAllocatedCents = 0;
        let totalAdvancesRecoupedCents = 0;
        let totalTaxWithheldCents = 0;
        let totalNetPayableCents = 0;

        const partyDistributions = args.parties.map((party, index) => {
            // Allocate share based on percentage
            let allocatedCents = Math.round(distributablePoolCents * (party.percentage / 100));
            // Prevent rounding leak on last item if distributablePool > 0
            if (index === args.parties.length - 1 && distributablePoolCents > 0) {
                const allocatedSoFar = totalAllocatedCents;
                allocatedCents = Math.max(0, distributablePoolCents - allocatedSoFar);
            }
            totalAllocatedCents += allocatedCents;

            // Recoup party advance
            const advancePaidCents = Math.round((party.advancePaid || 0) * 100);
            const advanceRecoupedCents = Math.min(allocatedCents, advancePaidCents);
            const remainingAdvanceCents = advancePaidCents - advanceRecoupedCents;
            totalAdvancesRecoupedCents += advanceRecoupedCents;

            const postRecoupmentCents = allocatedCents - advanceRecoupedCents;

            // Tax withholding
            const withholdingRate = (party.taxWithholdingPercent || 0) / 100;
            const taxWithheldCents = Math.round(postRecoupmentCents * withholdingRate);
            totalTaxWithheldCents += taxWithheldCents;

            const netPayableCents = postRecoupmentCents - taxWithheldCents;
            totalNetPayableCents += netPayableCents;

            const netPayable = netPayableCents / 100;
            const requires1099 = netPayable >= 600;

            return {
                name: party.name,
                role: party.role,
                percentage: party.percentage,
                shareType: party.shareType || 'net_profit',
                allocatedGross: allocatedCents / 100,
                advanceOriginal: advancePaidCents / 100,
                advanceRecouped: advanceRecoupedCents / 100,
                advanceRemaining: remainingAdvanceCents / 100,
                taxWithholdingPercent: party.taxWithholdingPercent || 0,
                taxWithheld: taxWithheldCents / 100,
                netPayable,
                requires1099,
                payoutMethod: party.payoutMethod || 'Direct Deposit / ACH'
            };
        });

        const total1099Count = partyDistributions.filter(p => p.requires1099).length;

        const resultData = {
            trackTitle: args.trackTitle,
            currency,
            summary: {
                grossRevenue: grossCents / 100,
                distributionFeePercent: distFeePercent,
                distributionFeeAmount: distributionFeeCents / 100,
                postDistributorRevenue: postDistributorCents / 100,
                totalRecoupableExpenses: totalExpensesCents / 100,
                expensesRecoupedThisPeriod: expensesRecoupedCents / 100,
                remainingUnrecoupedExpenses: unrecoupedExpensesCents / 100,
                distributablePool: distributablePoolCents / 100,
                totalAdvancesRecouped: totalAdvancesRecoupedCents / 100,
                totalTaxWithheld: totalTaxWithheldCents / 100,
                totalNetPayable: totalNetPayableCents / 100,
                isFullyRecouped: unrecoupedExpensesCents === 0
            },
            expenses: itemizedExpenses,
            partyDistributions,
            compliance: {
                total1099FormsRequired: total1099Count,
                auditStandard: 'indii Golden File Royalty Waterfall v2.1',
                precision: 'integer-cents deterministic arithmetic',
                timestamp: new Date().toISOString()
            }
        };

        const message = `Deterministic royalty distribution calculated for "${args.trackTitle}": Gross ${currency} $${(grossCents / 100).toFixed(2)}, Distributable Pool $${(distributablePoolCents / 100).toFixed(2)}, Net Payable $${(totalNetPayableCents / 100).toFixed(2)}. ${total1099Count} party(s) flagged for 1099 tax reporting.`;

        return toolSuccess(resultData, message);
    })
} satisfies Record<string, AnyToolFunction>;

// Aliases
export const {
    analyze_receipt,
    audit_distribution,
    forecast_revenue,
    generate_schedule_c,
    calculate_waterfall,
    initiate_split_escrow,
    compare_budget_vs_actuals,
    predict_daily_royalties,
    convert_multi_currency,
    onboard_stripe_connect,
    request_tax_forms,
    normalize_distributor_statements,
    royalty_distribution_calculator
} = FinanceTools;
